// frontend/src/pages/Admin/Annuaire.tsx
// Annuaire des avocats — CNB + Ordre de Paris + SIRENE + abonnés/prospects,
// synchronisé chaque matin par le cron smartdata sync_annuaire_avocats.py
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Page from 'pages/Page';

import { ChainlitContext, useAuth } from '@chainlit/react-client';

import { Loader } from '@/components/Loader';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Avocat {
  id: string;
  nom: string;
  prenom: string;
  barreau: string;
  cabinet?: string;
  structure?: string;
  ville?: string;
  cp?: string;
  specialites?: string[];
  activite_dominante?: string[];
  mode_exercice?: string;
  date_serment?: string | null;
  tranche_effectif?: string;
  sources?: string[];
  status?: string;
  first_seen?: string;
  is_baseline?: boolean;
  abonne_email?: string;
  prospect_email?: string;
  prospect_status?: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  contact_site?: string | null;
  contact?: Contact | null;
  [key: string]: unknown;
}

interface ContactItem {
  value: string;
  source_url: string;
}

interface Contact {
  site_url?: string | null;
  bar_url?: string | null;
  emails: ContactItem[];
  phones: ContactItem[];
  status: 'found' | 'site_only' | 'not_found';
  searched_at: string;
}

interface Facet {
  value: string;
  count: number;
}

interface Run {
  run_at: string;
  total: number;
  nouveaux: number;
  absents: number;
  baseline: boolean;
  paris_matched: number;
  sirene_joined: number;
  sirene_pending: number;
  cnb_file: string;
  duration_s: number;
}

interface Stats {
  ready: boolean;
  inscrits: number;
  absents: number;
  nouveaux_7j: number;
  nouveaux_30j: number;
  avec_email: number;
  barreaux: Facet[];
  specialites: Facet[];
  modes_exercice: Facet[];
  sources: Facet[];
  last_run: Run | null;
}

interface Filters {
  q: string;
  barreau: string;
  specialite: string;
  mode_exercice: string;
  source: string;
  status: string;
  nouveaux_jours: string;
  serment_depuis: string;
  has_email: string;
}

const EMPTY_FILTERS: Filters = {
  q: '',
  barreau: '',
  specialite: '',
  mode_exercice: '',
  source: '',
  status: 'inscrit',
  nouveaux_jours: '',
  serment_depuis: '',
  has_email: ''
};

const SOURCE_LABELS: Record<string, string> = {
  cnb: 'CNB',
  paris: 'Ordre de Paris',
  sirene: 'SIRENE',
  abonne: 'Abonné',
  prospect: 'Prospect'
};

const SOURCE_STYLES: Record<string, string> = {
  cnb: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  paris: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  sirene:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  abonne:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  prospect: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
};

const DETAIL_LABELS: [string, string][] = [
  ['barreau', 'Barreau'],
  ['cabinet', 'Cabinet (CNB)'],
  ['structure', 'Structure (Ordre de Paris)'],
  ['forme_structure', 'Forme'],
  ['mode_exercice', "Mode d'exercice"],
  ['toque', 'Toque'],
  ['adresse', 'Adresse'],
  ['cp', 'Code postal'],
  ['ville', 'Ville'],
  ['specialites', 'Spécialités'],
  ['activite_dominante', 'Activité dominante'],
  ['mandats', 'Mandats'],
  ['date_serment', 'Date de serment'],
  ['langues', 'Langues'],
  ['siren', 'SIREN'],
  ['entreprise_nom', 'Entreprise (SIRENE)'],
  ['naf', 'NAF'],
  ['tranche_effectif', 'Effectif'],
  ['entreprise_creation', 'Création'],
  ['entreprise_etat', 'État'],
  ['nb_etablissements', 'Établissements'],
  ['abonne_email', 'E-mail abonné SmartLawyer'],
  ['abonne_plan', 'Plan abonné'],
  ['prospect_email', 'E-mail prospect'],
  ['prospect_status', 'Statut prospect'],
  ['status', 'Statut annuaire'],
  ['first_seen', 'Vu la première fois'],
  ['absent_since', 'Absent depuis'],
  ['cnb_file', 'Fichier CNB']
];

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';

const fmtValue = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  return String(v);
};

// ── Petits composants ─────────────────────────────────────────────────────────

const StatCard = ({
  label,
  value,
  sub,
  color = 'text-foreground'
}: {
  label: string;
  value: number | string;
  sub?: string;
  color?: string;
}) => (
  <div className="bg-card border border-border rounded-lg p-4">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>
      {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
    </div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const FilterSelect = ({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-muted-foreground">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring max-w-[220px]"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const facetOptions = (all: string, facets: Facet[] = []) => [
  { value: '', label: all },
  ...facets.map((f) => ({
    value: f.value,
    label: `${f.value} (${f.count.toLocaleString('fr-FR')})`
  }))
];

// ── Composant principal ───────────────────────────────────────────────────────

const AnnuaireContent = () => {
  const { isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();

  const [stats, setStats] = useState<Stats | null>(null);
  const [items, setItems] = useState<Avocat[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState<Avocat | null>(null);

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, isReady, navigate]);

  // Recherche texte : appliquée 400 ms après la dernière frappe
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: query })), 400);
    return () => clearTimeout(t);
  }, [query]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    (Object.keys(filters) as (keyof Filters)[]).forEach((k) => {
      if (k === 'status' || filters[k]) p.set(k, filters[k]);
    });
    return p;
  }, [filters]);

  useEffect(() => {
    if (!isAuthenticated || !isReady) return;
    fetch(apiClient.buildEndpoint('/api/annuaire/stats'), {
      credentials: 'include'
    })
      .then((r) => r.json())
      .then(setStats)
      .catch(() => setStats(null));
  }, [isAuthenticated, isReady, apiClient]);

  const load = useCallback(
    (after: string | null) => {
      if (!isAuthenticated || !isReady) return;
      setLoading(true);
      setError('');
      const p = new URLSearchParams(params);
      p.set('limit', '50');
      if (after) p.set('cursor', after);
      fetch(apiClient.buildEndpoint(`/api/annuaire/?${p}`), {
        credentials: 'include'
      })
        .then((r) => {
          if (!r.ok) throw new Error(`Erreur API (${r.status})`);
          return r.json();
        })
        .then((d) => {
          setItems((prev) => (after ? [...prev, ...d.items] : d.items));
          setTotal(d.total ?? 0);
          setCursor(d.next_cursor ?? null);
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [isAuthenticated, isReady, apiClient, params]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const openDetail = (id: string) => {
    fetch(apiClient.buildEndpoint(`/api/annuaire/${id}`), {
      credentials: 'include'
    })
      .then((r) => r.json())
      .then(setDetail)
      .catch(() => setDetail(null));
  };

  const set = (k: keyof Filters) => (v: string) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const exportUrl = apiClient.buildEndpoint(
    `/api/annuaire/export.csv?${params}`
  );
  const run = stats?.last_run;

  return (
    <div className="flex flex-col gap-6 p-6 max-w-screen-xl mx-auto w-full h-full overflow-y-auto [&>*]:shrink-0">
      {/* Titre */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Annuaire des avocats
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            CNB et Ordre des avocats de Paris (data.gouv.fr), croisés avec
            SIRENE, les abonnés et les prospects — synchronisé chaque matin à 6
            h.
          </p>
        </div>
        <a
          href={exportUrl}
          className="text-sm border border-border rounded-lg px-3 py-1.5 hover:bg-muted"
        >
          ⬇ Exporter la sélection (CSV)
        </a>
      </div>

      {/* Stats */}
      {stats && !stats.ready && (
        <div className="bg-card border border-border rounded-lg p-4 text-sm text-muted-foreground">
          L'annuaire n'a pas encore été synchronisé : il sera rempli au prochain
          passage du cron (6 h).
        </div>
      )}
      {stats?.ready && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Avocats inscrits" value={stats.inscrits} />
          <StatCard
            label="Nouveaux (7 j)"
            value={stats.nouveaux_7j}
            sub={`30 j : ${stats.nouveaux_30j.toLocaleString('fr-FR')}`}
            color="text-emerald-600"
          />
          <StatCard
            label="Sortis de l'annuaire"
            value={stats.absents}
            sub="radiés, omis ou changés de barreau"
          />
          <StatCard
            label="Avec e-mail connu"
            value={stats.avec_email}
            sub="abonnés ou prospects rapprochés"
            color="text-blue-600"
          />
          <StatCard
            label="Dernière synchro"
            value={run ? fmtDate(run.run_at) : '—'}
            sub={
              run
                ? `${
                    run.cnb_file
                  } · SIRENE en attente : ${run.sirene_pending.toLocaleString(
                    'fr-FR'
                  )}`
                : undefined
            }
          />
        </div>
      )}

      {/* Filtres */}
      <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <label className="text-xs text-muted-foreground">Recherche</label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Nom, prénom, cabinet, ville…"
            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <FilterSelect
          label="Barreau"
          value={filters.barreau}
          onChange={set('barreau')}
          options={facetOptions('Tous les barreaux', stats?.barreaux)}
        />
        <FilterSelect
          label="Spécialité"
          value={filters.specialite}
          onChange={set('specialite')}
          options={facetOptions('Toutes', stats?.specialites)}
        />
        <FilterSelect
          label="Mode d'exercice (Paris)"
          value={filters.mode_exercice}
          onChange={set('mode_exercice')}
          options={facetOptions('Tous', stats?.modes_exercice)}
        />
        <FilterSelect
          label="Source"
          value={filters.source}
          onChange={set('source')}
          options={[
            { value: '', label: 'Toutes' },
            ...(stats?.sources ?? []).map((f) => ({
              value: f.value,
              label: `${
                SOURCE_LABELS[f.value] ?? f.value
              } (${f.count.toLocaleString('fr-FR')})`
            }))
          ]}
        />
        <FilterSelect
          label="Arrivée"
          value={filters.nouveaux_jours}
          onChange={set('nouveaux_jours')}
          options={[
            { value: '', label: 'Toutes' },
            { value: '7', label: 'Nouveaux (7 j)' },
            { value: '30', label: 'Nouveaux (30 j)' }
          ]}
        />
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">
            Serment depuis
          </label>
          <input
            type="date"
            value={filters.serment_depuis}
            onChange={(e) => set('serment_depuis')(e.target.value)}
            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground"
          />
        </div>
        <FilterSelect
          label="E-mail"
          value={filters.has_email}
          onChange={set('has_email')}
          options={[
            { value: '', label: 'Indifférent' },
            { value: 'true', label: 'E-mail connu' },
            { value: 'false', label: 'Sans e-mail' }
          ]}
        />
        <FilterSelect
          label="Statut"
          value={filters.status}
          onChange={set('status')}
          options={[
            { value: 'inscrit', label: 'Inscrits' },
            { value: 'absent', label: 'Sortis' },
            { value: '', label: 'Tous' }
          ]}
        />
        <button
          onClick={() => {
            setQuery('');
            setFilters(EMPTY_FILTERS);
          }}
          className="text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 border border-border rounded-lg"
        >
          Réinitialiser
        </button>
      </div>

      {/* Liste */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-4 py-2 text-xs text-muted-foreground border-b border-border">
          {total.toLocaleString('fr-FR')} avocat{total !== 1 ? 's' : ''}
        </div>
        {error && <div className="p-4 text-sm text-rose-600">{error}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">Avocat</th>
                <th className="text-left px-4 py-2">Barreau</th>
                <th className="text-left px-4 py-2">Cabinet</th>
                <th className="text-left px-4 py-2">Ville</th>
                <th className="text-left px-4 py-2">Spécialités / activité</th>
                <th className="text-left px-4 py-2">Serment</th>
                <th className="text-left px-4 py-2">Sources</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => openDetail(a.id)}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                >
                  <td className="px-4 py-2">
                    <div className="font-medium">
                      {a.prenom} {a.nom}
                      {a.is_baseline === false && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          nouveau
                        </span>
                      )}
                    </div>
                    {a.mode_exercice && (
                      <div className="text-xs text-muted-foreground">
                        {a.mode_exercice}
                      </div>
                    )}
                    {(a.abonne_email ||
                      a.prospect_email ||
                      a.contact_email) && (
                      <div className="text-xs text-blue-600">
                        {a.abonne_email || a.prospect_email || a.contact_email}
                      </div>
                    )}
                    {a.contact_phone && (
                      <div className="text-xs text-muted-foreground">
                        ☎ {a.contact_phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">{a.barreau}</td>
                  <td className="px-4 py-2">
                    <div>{a.structure || a.cabinet || '—'}</div>
                    {a.tranche_effectif && (
                      <div className="text-xs text-muted-foreground">
                        Effectif : {a.tranche_effectif}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {a.ville}{' '}
                    {a.cp && (
                      <span className="text-muted-foreground">({a.cp})</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs max-w-[260px]">
                    {fmtValue([
                      ...(a.specialites ?? []),
                      ...(a.activite_dominante ?? [])
                    ])}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {fmtDate(a.date_serment)}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(a.sources ?? []).map((s) => (
                        <span
                          key={s}
                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                            SOURCE_STYLES[s] ?? ''
                          }`}
                        >
                          {SOURCE_LABELS[s] ?? s}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && (
          <div className="p-4 flex justify-center">
            <Loader />
          </div>
        )}
        {!loading && cursor && (
          <div className="p-3 flex justify-center border-t border-border">
            <button
              onClick={() => load(cursor)}
              className="text-sm border border-border rounded-lg px-4 py-1.5 hover:bg-muted"
            >
              Charger plus
            </button>
          </div>
        )}
        {!loading && !items.length && !error && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Aucun avocat ne correspond à ces filtres.
          </div>
        )}
      </div>

      {/* Fiche détaillée */}
      {detail && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex justify-end"
          onClick={() => setDetail(null)}
        >
          <div
            className="w-full max-w-md h-full bg-background border-l border-border p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold">
                {detail.prenom} {detail.nom}
              </h2>
              <button
                onClick={() => setDetail(null)}
                className="text-muted-foreground hover:text-foreground text-sm"
              >
                Fermer
              </button>
            </div>
            <dl className="mt-4 space-y-2 text-sm">
              {DETAIL_LABELS.filter(([k]) => fmtValue(detail[k]) !== '—').map(
                ([k, label]) => (
                  <div key={k} className="grid grid-cols-[140px_1fr] gap-2">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd>
                      {[
                        'first_seen',
                        'absent_since',
                        'date_serment',
                        'entreprise_creation'
                      ].includes(k)
                        ? fmtDate(detail[k] as string)
                        : fmtValue(detail[k])}
                    </dd>
                  </div>
                )
              )}
            </dl>

            {/* Coordonnées trouvées par la prospection (pages réelles, source citée) */}
            <h3 className="mt-6 text-sm font-semibold">Coordonnées</h3>
            {!detail.contact ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Pas encore recherchées (50 avocats par jour, à 9 h).
              </p>
            ) : detail.contact.status === 'not_found' ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Aucun site ni contact trouvé le{' '}
                {fmtDate(detail.contact.searched_at)}.
              </p>
            ) : (
              <div className="mt-2 space-y-2 text-sm">
                {detail.contact.site_url && (
                  <a
                    href={detail.contact.site_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-blue-500 hover:underline"
                  >
                    🌐 {detail.contact.site_url}
                  </a>
                )}
                {[
                  ...detail.contact.emails.map((c) => ({
                    ...c,
                    icon: '✉',
                    href: `mailto:${c.value}`
                  })),
                  ...detail.contact.phones.map((c) => ({
                    ...c,
                    icon: '☎',
                    href: `tel:${c.value.replace(/\s/g, '')}`
                  }))
                ].map((c) => (
                  <div
                    key={c.value}
                    className="flex flex-wrap items-baseline gap-2"
                  >
                    <a href={c.href} className="text-blue-500 hover:underline">
                      {c.icon} {c.value}
                    </a>
                    <a
                      href={c.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-muted-foreground hover:underline truncate max-w-[220px]"
                    >
                      source :{' '}
                      {c.source_url.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  Recherche du {fmtDate(detail.contact.searched_at)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Export ────────────────────────────────────────────────────────────────────

const Annuaire = () => (
  <Page>
    <AnnuaireContent />
  </Page>
);

export default Annuaire;
